/* eslint-disable no-console,class-methods-use-this */
import { Banner, BannerState } from '@/modules/adv/banner'
import { OutOfPageBanner } from '@/modules/adv/out-of-page-banner'
import { GAM_SOURCE } from '@/modules/adv/providers/google-ads'
import { AdProviderNames, PlatformType } from '@/types'
import { loadScript } from '@/utils/loadScript'

import {
    AdProvider,
    type AdProviderConfig,
    AdProviderStatus,
    AdUnitConfig,
    NotImplementedError,
    type OutOfPageAdType,
    type PageAdOptions,
} from '../../'
import { renderWrapper } from './render-wrapper'

export const IMA_SCRIPT_SRC = 'https://imasdk.googleapis.com/js/sdkloader/ima3.js'

const debugLog = (...args: unknown[]): void => {
    console.log('[GoogleInstream]', ...args)
}
const DEFAULT_TIMEOUT = 1_000

type RequestContext = {
    bannerType: OutOfPageAdType
}

type InstreamSupport = 'no' | 'muted' | 'full'

export class GoogleInstreamAdsService implements AdProvider {
    public readonly serviceStatus: Promise<AdProviderStatus>

    private readonly platform?: PlatformType
    private readonly isWidget: boolean
    private readonly gamAccount
    private readonly auid
    private readonly adUnitConfig: AdUnitConfig | undefined
    private readonly useOnlyConfig: boolean
    private readonly adUrl: string

    private adsLoader: google.ima.AdsLoader | null = null
    private adDisplayContainer: google.ima.AdDisplayContainer | null = null
    private wrapperControls

    private videoWrapper: HTMLDivElement
    private videoContainer: HTMLDivElement

    private videoPlayer: HTMLVideoElement
    private isVideoMuted = false
    private countdownTimer: ReturnType<typeof setInterval> | undefined

    private correlator: string
    private adStartTime: number | null = null

    private preloadedBanners: Map<OutOfPageAdType, OutOfPageBanner> = new Map()
    private currentTargeting: Record<string, string | string[]> = {}

    constructor({
        targeting = {},
        platform,
        gamAccount,
        auid,
        isWidget,
        adUnitConfig,
        footerHeight,
        adUrl,
        useOnlyConfig,
    }: AdProviderConfig & { gamAccount: string; footerHeight?: string; adUrl?: string }) {
        this.platform = platform
        this.isWidget = isWidget
        this.correlator = `${Date.now()}`
        const app = renderWrapper({ footerHeight })
        this.wrapperControls = app.controls
        this.videoWrapper = app.wrapper
        this.gamAccount = gamAccount
        this.currentTargeting = targeting
        this.auid = auid
        this.adUrl = adUrl || 'https://pubads.g.doubleclick.net/gampad/ads'

        if (adUnitConfig) {
            this.adUnitConfig = adUnitConfig
        }

        this.useOnlyConfig = Boolean(adUnitConfig && useOnlyConfig)

        this.videoContainer = this.videoWrapper.querySelector('[data-id="google-instream-banner"]') as HTMLDivElement
        this.videoPlayer = this.videoWrapper.querySelector('[data-id="google-instream-player"]') as HTMLVideoElement

        this.serviceStatus = this.loadResources()
    }

    private async loadResources(): Promise<AdProviderStatus> {
        try {
            await this.checkAutoplaySupport()

            await loadScript({ src: GAM_SOURCE, useExisting: true })

            window.googletag = window.googletag || { cmd: [] }
            window.googletag.cmd = window.googletag.cmd || []

            return new Promise((resolve) => {
                window.googletag.cmd.push(async () => {
                    await loadScript({ src: IMA_SCRIPT_SRC, timeoutMs: 10_000, useExisting: true })

                    google.ima.settings.setVpaidMode(google.ima.ImaSdkSettings.VpaidMode.ENABLED)

                    const adsContainer = (this.adDisplayContainer = new google.ima.AdDisplayContainer(
                        this.videoContainer,
                        this.videoPlayer,
                    ))
                    this.adsLoader = new google.ima.AdsLoader(adsContainer)

                    this.adDisplayContainer?.initialize() // should be initiated by click
                    // not a big deal, but ios may be buggy

                    this.adsLoader.addEventListener(
                        google.ima.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED,
                        this.onAdsLoaded.bind(this),
                        false,
                    )

                    this.adsLoader.addEventListener(
                        google.ima.AdErrorEvent.Type.AD_ERROR,
                        (event: google.ima.AdErrorEvent) => {
                            debugLog('AdsLoader error', event, event.getError())
                            const ctx = event.getUserRequestContext() as RequestContext
                            const banner = this.preloadedBanners.get(ctx.bannerType)
                            if (banner) {
                                banner.triggerEmpty()
                            }
                        },
                        false,
                    )

                    resolve(AdProviderStatus.online)
                })
            })
        } catch (error) {
            console.error('Google Instream error: ', error)
            return Promise.reject(AdProviderStatus.offline)
        }
    }

    private onAdsLoaded(event: google.ima.AdsManagerLoadedEvent) {
        const adsRenderingSettings = new google.ima.AdsRenderingSettings()
        const ctx = event.getUserRequestContext() as RequestContext

        const adsManager = event.getAdsManager({ currentTime: 0, duration: 0 }, adsRenderingSettings)

        if (this.isVideoMuted) {
            adsManager.setVolume(0)
        }

        const resizeObserver = new ResizeObserver(() => {
            requestAnimationFrame(() => {
                adsManager?.resize(
                    Math.floor(this.videoContainer.clientWidth),
                    Math.floor(this.videoContainer.clientHeight),
                    google.ima.ViewMode.NORMAL,
                )
            })
        })

        resizeObserver.observe(this.videoContainer)

        adsManager.addEventListener(google.ima.AdEvent.Type.AD_BREAK_FETCH_ERROR, (adEvent: google.ima.AdEvent) => {
            debugLog('AD_BREAK_FETCH_ERROR: no proper handling, no retry', adEvent)

            adsManager.destroy()
            resizeObserver.disconnect()

            this.getCurrentBanner(ctx.bannerType)?.triggerEmpty()
        })

        adsManager.addEventListener(google.ima.AdErrorEvent.Type.AD_ERROR, (adEvent: google.ima.AdErrorEvent) => {
            const error = adEvent.getError()

            debugLog(
                'AD_ERROR, no retry',
                adEvent,
                error?.getErrorCode(),
                error?.getVastErrorCode(),
                error?.getMessage(),
            )

            adsManager.destroy()
            resizeObserver.disconnect()

            this.getCurrentBanner(ctx.bannerType)?.triggerEmpty()
        })

        adsManager.addEventListener(google.ima.AdEvent.Type.CLICK, () => {
            this.getCurrentBanner(ctx.bannerType)?.triggerClicked()

            if (!adsManager.getAdSkippableState()) {
                const continueAd = () => {
                    this.wrapperControls.switchOverlayVisibility(false)
                    adsManager.resume()
                }
                this.wrapperControls.switchOverlayVisibility(true)
                this.wrapperControls.setContinueAdHandler(continueAd)
            }
            this.stopTimer()
        })

        adsManager.addEventListener(google.ima.AdEvent.Type.RESUMED, () => {
            const banner = this.getCurrentBanner(ctx.bannerType)
            if (banner) {
                this.startTimer(adsManager, banner)
            }
        })

        adsManager.addEventListener(google.ima.AdEvent.Type.IMPRESSION, () => {
            this.getCurrentBanner(ctx.bannerType)?.triggerRendered()
        })

        adsManager.addEventListener(google.ima.AdEvent.Type.LOADED, (adEvent: google.ima.AdEvent) => {
            debugLog('LOADED', adEvent, ctx)

            const banner = this.getCurrentBanner(ctx.bannerType)
            const duration = adEvent.getAd()?.getDuration()

            if (duration && banner) {
                banner.instreamDuration = duration
            }

            banner?.triggerReady(() => {
                adsManager.start()
            })
        })

        adsManager.addEventListener(google.ima.AdEvent.Type.STARTED, (adEvent: google.ima.AdEvent) => {
            debugLog('STARTED', adEvent)

            const banner = this.getCurrentBanner(ctx.bannerType)

            if (banner) {
                this.adStartTime = Date.now()
                this.startTimer(adsManager, banner)

                this.videoWrapper.style.opacity = '1'
                this.videoWrapper.style.pointerEvents = 'auto'
                this.videoWrapper.style.zIndex = '2147483647' // max z-index to be on top of everything

                banner.triggerViewable()
            }
        })

        const endAd = (adEvent: google.ima.AdEvent) => {
            debugLog(adEvent.type, adEvent)

            this.videoWrapper.style.removeProperty('opacity')
            this.videoWrapper.style.removeProperty('pointer-events')
            this.videoWrapper.style.removeProperty('z-index')

            const banner = this.getCurrentBanner(ctx.bannerType)
            if (banner && this.adStartTime !== null) {
                banner.watchedDuration = Math.round((Date.now() - this.adStartTime) / 1000)
                this.adStartTime = null
            }

            if (ctx.bannerType === 'rewarded') {
                banner?.triggerRewarded({})
            }

            adsManager.stop()
            adsManager.destroy()
            resizeObserver.disconnect()

            banner?.triggerClosed()
        }

        adsManager.addEventListener(google.ima.AdEvent.Type.ALL_ADS_COMPLETED, endAd)

        adsManager.addEventListener(google.ima.AdEvent.Type.USER_CLOSE, endAd)

        adsManager.addEventListener(google.ima.AdEvent.Type.COMPLETE, endAd)

        adsManager.init(
            Math.floor(this.videoContainer.clientWidth),
            Math.floor(this.videoContainer.clientHeight),
            google.ima.ViewMode.NORMAL,
        )
    }

    private async preloadAds(type: OutOfPageAdType, isAvailable: InstreamSupport): Promise<void> {
        if (this.preloadedBanners.has(type)) {
            debugLog('Skip preloadAds, another banner loading')
            return
        }

        try {
            await this.serviceStatus
        } catch (error) {
            throw new Error('Instream ads: Service not initialized', { cause: error })
        }

        const { adsLoader, videoContainer } = this

        const adUnit = this.getAdUnitByType(type)

        const banner = new OutOfPageBanner({
            provider: AdProviderNames.GOOGLE_INSTREAM,
            adUnit,
        })

        if (!videoContainer) {
            throw new Error('Instream ads: No video container found.')
        }

        this.preloadedBanners.set(type, banner)

        const adsRequest = new google.ima.AdsRequest()

        const targeting = this.prepareCurrentTargeting()
        const hru = Array.isArray(this.currentTargeting.hru) ? this.currentTargeting.hru[0] : this.currentTargeting.hru

        const params: Record<string, string> = {
            iu: adUnit,
            description_url: 'https://playgama.com',
            url: 'https://playgama.com',
            referer: 'https://playgama.com',
            content_page_url: `https://playgama.com/game/${hru}`,
            placement_id: hru,
            ppid: this.auid,
            sz: '400x300|640x480',
            vpos: 'preroll',
            gdfp_req: '1',
            output: 'vast',
            unviewed_position_start: '1',
            env: 'vp',
            impl: 's',
            correlator: this.correlator,
            vpmute: isAvailable === 'muted' ? '1' : '0',
        }
        if (targeting) {
            params.cust_params = targeting
        }

        let adTagUrl = this.adUrl
        let divider = '?'
        if (adTagUrl.indexOf(divider) !== -1) {
            divider = '&'
        }

        adTagUrl += `${divider}${Object.entries(params)
            .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
            .join('&')}`

        adsRequest.adTagUrl = adTagUrl

        adsRequest.linearAdSlotWidth = adsRequest.nonLinearAdSlotWidth = Math.floor(videoContainer.clientWidth)
        adsRequest.linearAdSlotHeight = adsRequest.nonLinearAdSlotHeight = Math.floor(videoContainer.clientHeight)

        adsRequest.forceNonLinearFullSlot = true

        adsLoader?.requestAds(adsRequest, { bannerType: type })
    }

    public async prepareOutOfPageAd(type: OutOfPageAdType, timeout = DEFAULT_TIMEOUT): Promise<OutOfPageBanner> {
        debugLog('prepareOutOfPageAd', type, timeout)

        if (!this.preloadedBanners.has(type)) {
            const isAvailable = await this.checkAutoplaySupport()

            if (isAvailable === 'no') {
                return Promise.reject(new Error('Instream ads: Cannot use autoplay currently'))
            }

            await this.preloadAds(type, isAvailable) // waiting only service initialization - should be fast
        }

        const banner = this.preloadedBanners.get(type)

        if (!banner) {
            throw new Error('Instream ads: Failed to create banner')
        }

        if (banner.state === BannerState.empty) {
            setTimeout(() => {
                banner.triggerEmpty()
                this.preloadedBanners.delete(type)
            }, 16)
        }

        if (banner.state === BannerState.loading) {
            const timeoutId = setTimeout(() => {
                setTimeout(() => {
                    banner.triggerEmpty()
                    this.resetLoadingBanner(type) // will be waiting next call
                }, 16)
            }, timeout)

            banner.addEventListener('ready', () => {
                clearTimeout(timeoutId)
            })

            banner.addEventListener('empty', () => {
                clearTimeout(timeoutId)
                this.preloadedBanners.delete(type)
            })
        }

        if (banner.state === BannerState.ready) {
            setTimeout(() => {
                banner.triggerReady()
            }, 16)
        }

        banner.addEventListener('closed', () => {
            this.preloadedBanners.delete(type)
        })

        return banner
    }

    public requestPageAd(options: PageAdOptions): Promise<Banner> {
        debugLog('requestPageAd', options)
        return Promise.reject(new NotImplementedError())
    }

    public updateTargeting(targeting: AdProviderConfig['targeting'] = {}) {
        Object.entries(targeting).forEach(([key, value]) => {
            if (value) {
                this.currentTargeting[key] = value
            } else if (this.currentTargeting[key]) {
                delete this.currentTargeting[key]
            }
        })
    }

    public getAdUnitByType(type: OutOfPageAdType): string {
        const prefix = this.isWidget ? 'widget_' : ''

        const isMobile = this.platform === 'ios' || this.platform === 'android'
        const simplifiedPlatform = isMobile ? 'mobile' : 'desktop'
        const suffix = `_${simplifiedPlatform}`

        const unitFromConfig = this.adUnitConfig?.[simplifiedPlatform]?.[type]
        if (unitFromConfig) {
            return `${this.gamAccount}/${unitFromConfig}`
        }

        if (this.useOnlyConfig) {
            throw new Error('No such ad unit in this provider')
        }

        const units: Record<string, string> = {
            rewarded: 'instream_rewarded',
            rewarded_2: 'instream_rewarded_2',
            interstitial: 'instream',
            interstitial_preroll: 'instream_preroll',
            interstitial_preroll_2: 'instream_preroll_2',
        }

        return `${this.gamAccount}/${prefix}${units[type]}${suffix}`
    }

    private startTimer(adsManager: google.ima.AdsManager, banner: OutOfPageBanner) {
        if (this.countdownTimer) {
            clearInterval(this.countdownTimer)
        }
        const countdown = () => {
            const timeRemaining = adsManager.getRemainingTime()
            banner.triggerTimer(Math.round(timeRemaining))

            if (timeRemaining < 1 && this.countdownTimer) {
                clearInterval(this.countdownTimer)
            }
        }

        countdown()

        this.countdownTimer = setInterval(countdown, 150)
    }

    private stopTimer() {
        if (this.countdownTimer) {
            clearInterval(this.countdownTimer)
        }
    }

    private resetLoadingBanner(type: OutOfPageAdType) {
        this.preloadedBanners.delete(type)
        const adUnit = this.getAdUnitByType(type)
        this.preloadedBanners.set(
            type,
            new OutOfPageBanner({
                provider: AdProviderNames.GOOGLE_INSTREAM,
                adUnit,
            }),
        )
    }

    private getCurrentBanner(type: OutOfPageAdType) {
        // can't be cached due to reset and sending empty banner
        return this.preloadedBanners.get(type)
    }

    private prepareCurrentTargeting() {
        return (
            Object.entries(this.currentTargeting)
                .map(([key, value]) => {
                    if (Array.isArray(value)) {
                        return `${key}=${value.join(',')}`
                    }
                    return `${key}=${value}`
                })
                .join('&') || ''
        )
    }

    private checkAutoplaySupport(): Promise<InstreamSupport> {
        this.videoPlayer.volume = 1
        this.videoPlayer.muted = false
        this.videoPlayer.removeAttribute('muted')
        const playPromise = this.videoPlayer.play()
        if (playPromise !== undefined) {
            return playPromise
                .then(() => {
                    this.videoPlayer.pause()
                    this.isVideoMuted = false
                    return 'full' as InstreamSupport
                })
                .catch(() => {
                    return this.checkMutedAutoplaySupport()
                })
        }
        return Promise.resolve('no')
    }

    private checkMutedAutoplaySupport(): Promise<InstreamSupport> {
        this.videoPlayer.volume = 0
        this.videoPlayer.muted = true
        const playPromise = this.videoPlayer.play()
        if (playPromise !== undefined) {
            return playPromise
                .then(() => {
                    this.videoPlayer.pause()
                    this.videoPlayer.setAttribute('muted', '1')
                    this.isVideoMuted = true
                    return 'muted' as InstreamSupport
                })
                .catch(() => {
                    return 'no' as InstreamSupport
                })
        }
        return Promise.resolve('no')
    }
}
